// web/wishlist.ts — the EVOLUTION WISHLIST: player-marked desired genes + progress.
//
// Players mark gene targets they're chasing — "a Legendary Mandible", "an Epic Wing" —
// and the game tracks how close they are. A wishlist target is a (gene TYPE, MINIMUM
// RARITY) pair: the coarsest unit every surface speaks natively, since box rewards,
// inventory rows and equipped genes are all keyed by type+rarity. A target is FULFILLED
// once you hold a gene of that type at or above the wished rarity; its progress climbs
// with the best rarity you currently hold, so the wishlist doubles as a personal
// progression goal the player sets for themselves.
//
// PURE PRESENTATION STATE — like web/profile.ts it lives in its own localStorage key and
// never touches the sim, the action log, determinism, or any hash, so it cannot affect
// replay. This module is pure logic + a thin persistence shim + string HTML builders;
// all DOM wiring lives in web/main.ts. Unit-tested headlessly (web/wishlist.test.mjs).

import { GENE_TYPE_NAME, RARITY_NAME } from '../src/sim/geneData.ts';

const WISHLIST_KEY = 'brood.wishlist.v1';
export const WISHLIST_VERSION = 1;

/** Keep the wishlist a focused set of goals, not a dumping ground. */
export const TARGET_CAP = 12;

/** One desired gene: a body-part TYPE wanted at (at least) this RARITY. */
export interface WishTarget {
  type: number;   // GENE_TYPE index (0..5)
  rarity: number; // RARITY index (0..4) — the MINIMUM rarity wished for
}

/** The whole persistent wishlist (its own localStorage island). */
export interface WishlistState {
  version: number;
  targets: WishTarget[]; // insertion order (newest appended)
}

// --- identity / naming -------------------------------------------------------

/** Stable key for a (type, rarity) target — used in Sets and data-attributes. */
export function targetKey(type: number, rarity: number): string {
  return `${type}:${rarity}`;
}

/** Inverse of `targetKey`; null on a malformed key. */
export function parseTargetKey(key: string): WishTarget | null {
  const m = /^(\d+):(\d+)$/.exec(key);
  if (!m) return null;
  return { type: Number(m[1]), rarity: Number(m[2]) };
}

/** Human label for a target, e.g. "Legendary Mandible". */
export function wishName(type: number, rarity: number): string {
  return `${RARITY_NAME[rarity] ?? '?'} ${GENE_TYPE_NAME[type] ?? 'Gene'}`;
}

function validTarget(t: unknown): t is WishTarget {
  if (typeof t !== 'object' || t === null) return false;
  const o = t as Record<string, unknown>;
  return (
    typeof o.type === 'number' && o.type >= 0 && o.type < GENE_TYPE_NAME.length &&
    typeof o.rarity === 'number' && o.rarity >= 0 && o.rarity < RARITY_NAME.length
  );
}

// --- persistence (mirrors web/profile.ts) ------------------------------------

export function defaultWishlist(): WishlistState {
  return { version: WISHLIST_VERSION, targets: [] };
}

function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    const k = '__brood_wishlist_probe__';
    s.setItem(k, '1');
    s.removeItem(k);
    return s;
  } catch {
    return null;
  }
}

export function loadWishlist(): WishlistState {
  const s = storage();
  if (!s) return defaultWishlist();
  const raw = s.getItem(WISHLIST_KEY);
  if (raw === null) return defaultWishlist();
  try {
    const parsed = JSON.parse(raw) as Partial<WishlistState>;
    if (typeof parsed !== 'object' || parsed === null) return defaultWishlist();
    const targets = Array.isArray(parsed.targets) ? parsed.targets.filter(validTarget) : [];
    // De-dup defensively (a hand-edited store could carry repeats) and cap.
    const seen = new Set<string>();
    const clean: WishTarget[] = [];
    for (const t of targets) {
      const k = targetKey(t.type, t.rarity);
      if (seen.has(k)) continue;
      seen.add(k);
      clean.push({ type: t.type, rarity: t.rarity });
      if (clean.length >= TARGET_CAP) break;
    }
    return { version: WISHLIST_VERSION, targets: clean };
  } catch {
    return defaultWishlist();
  }
}

export function writeWishlist(w: WishlistState): void {
  const s = storage();
  if (!s) return;
  try { s.setItem(WISHLIST_KEY, JSON.stringify(w)); } catch { /* quota / disabled — ignore */ }
}

// --- mutations (pure: operate on the passed state; caller persists) ----------

export function hasTarget(w: WishlistState, type: number, rarity: number): boolean {
  return w.targets.some((t) => t.type === type && t.rarity === rarity);
}

/** Add a target. Returns false if it's a duplicate, capped, or out of range. */
export function addTarget(w: WishlistState, type: number, rarity: number): boolean {
  if (!validTarget({ type, rarity })) return false;
  if (hasTarget(w, type, rarity)) return false;
  if (w.targets.length >= TARGET_CAP) return false;
  w.targets.push({ type, rarity });
  return true;
}

/** Remove a target. Returns true if one was removed. */
export function removeTarget(w: WishlistState, type: number, rarity: number): boolean {
  const i = w.targets.findIndex((t) => t.type === type && t.rarity === rarity);
  if (i < 0) return false;
  w.targets.splice(i, 1);
  return true;
}

/** Toggle a target; returns the resulting wished state (true = now on the list). */
export function toggleTarget(w: WishlistState, type: number, rarity: number): boolean {
  if (hasTarget(w, type, rarity)) { removeTarget(w, type, rarity); return false; }
  return addTarget(w, type, rarity);
}

/** The set of target keys — for O(1) "is this gene wished?" lookups in views. */
export function targetKeySet(w: WishlistState): Set<string> {
  const out = new Set<string>();
  for (const t of w.targets) out.add(targetKey(t.type, t.rarity));
  return out;
}

// --- progress ----------------------------------------------------------------

/** A held gene, reduced to the only fields wishlist progress needs. */
export interface HeldGene { type: number; rarity: number; }

export interface WishProgress {
  type: number;
  rarity: number;        // the wished (minimum) rarity
  name: string;          // "Legendary Mandible"
  bestRarity: number;    // best rarity currently held of this type, -1 if none owned
  ownedOfType: number;   // genes held of this type at any rarity
  ownedAtOrAbove: number;// genes held of this type at >= the wished rarity
  fulfilled: boolean;    // bestRarity >= wished rarity
  fraction: number;      // 0..1 progress toward the wished rarity (1 once fulfilled)
}

/**
 * Compute progress for every target against the player's held genes. Pure: same
 * inputs ⇒ same output. Progress walks the rarity ladder — holding nothing scores 0,
 * each rarity step closer raises the fraction, and reaching the wished rarity (or
 * better) fulfils it at 1.0.
 */
export function wishlistProgress(
  targets: readonly WishTarget[],
  held: readonly HeldGene[],
): WishProgress[] {
  // Best rarity + per-type tallies in a single pass over the inventory.
  const bestByType = new Map<number, number>();
  const countByType = new Map<number, number>();
  for (const g of held) {
    countByType.set(g.type, (countByType.get(g.type) ?? 0) + 1);
    const cur = bestByType.get(g.type);
    if (cur === undefined || g.rarity > cur) bestByType.set(g.type, g.rarity);
  }

  return targets.map((t) => {
    const best = bestByType.get(t.type) ?? -1;
    const ownedOfType = countByType.get(t.type) ?? 0;
    let atOrAbove = 0;
    for (const g of held) if (g.type === t.type && g.rarity >= t.rarity) atOrAbove++;
    const fulfilled = best >= t.rarity;
    // (best+1)/(rarity+1): none held → 0, each tier closer climbs, fulfilled → 1.
    const fraction = fulfilled ? 1 : best < 0 ? 0 : (best + 1) / (t.rarity + 1);
    return {
      type: t.type, rarity: t.rarity, name: wishName(t.type, t.rarity),
      bestRarity: best, ownedOfType, ownedAtOrAbove: atOrAbove,
      fulfilled, fraction: Math.max(0, Math.min(1, fraction)),
    };
  });
}

/** Targets satisfied by a freshly-acquired gene of (type, rarity) — for box reveals. */
export function wishesFulfilledBy(
  targets: readonly WishTarget[],
  type: number,
  rarity: number,
): WishTarget[] {
  return targets.filter((t) => t.type === type && rarity >= t.rarity);
}

// --- HTML builders (pure strings; the DOM wiring lives in main.ts) -----------

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'));
}

/** The add-a-target picker (gene type × minimum rarity + a button). */
function pickerHTML(): string {
  const types = GENE_TYPE_NAME.map((n, i) => `<option value="${i}">${esc(n)}</option>`).join('');
  // Default the rarity to Rare (2) — an aspirational, reachable target.
  const rarities = RARITY_NAME.map((n, i) => `<option value="${i}"${i === 2 ? ' selected' : ''}>${esc(n)}</option>`).join('');
  return (
    `<div class="wish-add">` +
      `<select id="wish-rarity" class="wish-sel" aria-label="Minimum rarity">${rarities}</select>` +
      `<select id="wish-type" class="wish-sel" aria-label="Gene type">${types}</select>` +
      `<button class="btn small btn-primary" data-wish-add type="button" title="Add this gene to your Evolution Wishlist">+ Desire</button>` +
    `</div>`
  );
}

/** One progress row: name, status, a fill bar, and a remove button. */
function rowHTML(p: WishProgress): string {
  const pct = Math.round(p.fraction * 100);
  const status = p.fulfilled
    ? `<span class="wish-status done">✓ Obtained${p.ownedAtOrAbove > 1 ? ` ×${p.ownedAtOrAbove}` : ''}</span>`
    : p.bestRarity < 0
      ? `<span class="wish-status">Not yet found</span>`
      : `<span class="wish-status">Best held: ${esc(RARITY_NAME[p.bestRarity] ?? '?')}</span>`;
  return (
    `<div class="wish-row rcol-${p.rarity}${p.fulfilled ? ' done' : ''}">` +
      `<div class="wish-row-head">` +
        `<span class="wish-name">${p.fulfilled ? '★' : '☆'} ${esc(p.name)}</span>` +
        `<button class="wish-remove" data-wish-remove="${targetKey(p.type, p.rarity)}" type="button" title="Remove from wishlist" aria-label="Remove ${esc(p.name)} from wishlist">✕</button>` +
      `</div>` +
      `<div class="wish-bar"><span style="width:${pct}%"></span></div>` +
      `<div class="wish-row-foot">${status}<span class="wish-pct">${pct}%</span></div>` +
    `</div>`
  );
}

/**
 * The full Wishlist section for the Inventory screen: header + picker + progress
 * rows (or an empty-state prompt). `count`/`cap` drive the header badge.
 */
export function wishlistSectionHTML(progress: readonly WishProgress[]): string {
  const done = progress.filter((p) => p.fulfilled).length;
  const head =
    `<div class="wish-head">` +
      `<span class="wish-title">✨ Evolution Wishlist</span>` +
      `<span class="wish-count muted small">${progress.length}/${TARGET_CAP}${progress.length ? ` · ${done} obtained` : ''}</span>` +
    `</div>`;
  const body = progress.length === 0
    ? `<div class="wish-empty muted small">Mark genes you're chasing — pick a type &amp; minimum rarity above, or tap ☆ on any gene below. Track your progress toward them here.</div>`
    : `<div class="wish-rows">${progress.map(rowHTML).join('')}</div>`;
  return `<section class="wishlist">${head}${pickerHTML()}${body}</section>`;
}

/** A compact read-only progress strip for the Armory (no controls). */
export function wishlistStripHTML(progress: readonly WishProgress[]): string {
  if (progress.length === 0) return '';
  const chips = progress.map((p) => {
    const pct = Math.round(p.fraction * 100);
    return (
      `<span class="wish-chip rcol-${p.rarity}${p.fulfilled ? ' done' : ''}" title="${esc(p.name)} — ${pct}%">` +
        `${p.fulfilled ? '★' : '☆'} ${esc(p.name)} <b>${pct}%</b>` +
      `</span>`
    );
  }).join('');
  return (
    `<div class="wish-strip">` +
      `<span class="wish-strip-title muted small">✨ Wishlist</span>` +
      `<div class="wish-strip-chips">${chips}</div>` +
    `</div>`
  );
}
